<template>
  <div class="fb">
    <div v-if="isSubmitted" class="fb-thanks">
      <div class="fb-thanks-icon">
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <div class="fb-thanks-body">
        <p class="fb-thanks-text">Thanks for the feedback.</p>
        <p v-if="existing" class="fb-thanks-meta">
          Sent on {{ formatDate(existing.created_at) }}
          <template v-if="existing.anonymous"> · anonymous</template>
        </p>
        <div v-if="existing && existing.rating" class="fb-thanks-stars">
          <svg
            v-for="n in 5"
            :key="n"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
            :class="{ on: n <= existing.rating }"
          >
            <polygon
              points="12 2 15 8.5 22 9.3 17 14.1 18.2 21 12 17.8 5.8 21 7 14.1 2 9.3 9 8.5 12 2"
            />
          </svg>
        </div>
        <p v-if="existing && existing.message" class="fb-thanks-message">
          “{{ existing.message }}”
        </p>
      </div>
    </div>

    <form v-else class="fb-form" @submit.prevent="submit">
      <div class="fb-stars">
        <button
          v-for="n in 5"
          :key="n"
          type="button"
          class="fb-star"
          :class="{ filled: n <= rating, hover: n <= hover }"
          @click="rating = n"
          @mouseenter="hover = n"
          @mouseleave="hover = 0"
          :aria-label="`${n} star${n > 1 ? 's' : ''}`"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="currentColor"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linejoin="round"
          >
            <polygon
              points="12 2 15 8.5 22 9.3 17 14.1 18.2 21 12 17.8 5.8 21 7 14.1 2 9.3 9 8.5 12 2"
            />
          </svg>
        </button>
      </div>

      <textarea
        v-model="message"
        class="fb-textarea"
        rows="4"
        placeholder="What worked? What could be better?"
        maxlength="2000"
      ></textarea>

      <div class="fb-actions">
        <label class="fb-anon">
          <input type="checkbox" v-model="anonymous" />
          <span class="fb-anon-track"><span class="fb-anon-thumb"></span></span>
          <span class="fb-anon-text">Send anonymously</span>
        </label>
        <button
          class="fb-submit"
          type="submit"
          :disabled="!canSubmit || submitting"
        >
          {{ submitting ? 'Sending' : 'Send feedback' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
export default {
  name: 'FeedbackTab',
  props: {
    initialRating: { type: Number, default: 0 },
    alreadySubmitted: { type: Boolean, default: false },
    existing: { type: Object, default: null },
  },
  data() {
    return {
      rating: this.initialRating,
      hover: 0,
      message: '',
      anonymous: false,
      submitting: false,
      localSubmitted: false,
    };
  },
  computed: {
    isSubmitted() {
      return this.alreadySubmitted || this.localSubmitted;
    },
    canSubmit() {
      return this.rating > 0 || this.message.trim().length > 0;
    },
  },
  watch: {
    initialRating(v) {
      if (v && !this.rating) this.rating = v;
    },
  },
  methods: {
    submit() {
      if (!this.canSubmit) return;
      this.submitting = true;
      const payload = {
        rating: this.rating,
        message: this.message.trim(),
        anonymous: this.anonymous,
      };
      this.$emit('submit', payload, (ok) => {
        this.submitting = false;
        if (ok !== false) this.localSubmitted = true;
      });
    },
    formatDate(iso) {
      try {
        return new Date(iso).toLocaleString();
      } catch (e) {
        return '';
      }
    },
  },
};
</script>

<style scoped>
.fb {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  color: #fff;
}

.fb-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 560px;
}

.fb-stars {
  display: flex;
  gap: 2px;
}
.fb-star {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: rgba(255, 255, 255, 0.18);
  transition: color 0.15s ease, transform 0.15s ease;
}
.fb-star:hover {
  transform: scale(1.05);
}
.fb-star.filled,
.fb-star.hover {
  color: #d3ca57;
}

.fb-textarea {
  width: 100%;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 12px 14px;
  color: #fff;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  resize: vertical;
  transition: border-color 0.15s ease;
  min-height: 96px;
}
.fb-textarea:focus {
  border-color: rgba(211, 202, 87, 0.45);
}
.fb-textarea::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.fb-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.fb-anon {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}
.fb-anon input {
  display: none;
}
.fb-anon-track {
  position: relative;
  width: 32px;
  height: 18px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  transition: background 0.15s ease;
  flex-shrink: 0;
}
.fb-anon-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 12px;
  height: 12px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.15s ease;
}
.fb-anon input:checked + .fb-anon-track {
  background: #d3ca57;
}
.fb-anon input:checked + .fb-anon-track .fb-anon-thumb {
  transform: translateX(14px);
  background: #0b0c14;
}
.fb-anon-text {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}

.fb-submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px 18px;
  background: #d3ca57;
  color: #0b0c14;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, opacity 0.15s ease;
}
.fb-submit:hover:not(:disabled) {
  background: #ddd56a;
}
.fb-submit:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.fb-thanks {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 20px;
  background: rgba(74, 222, 128, 0.05);
  border: 1px solid rgba(74, 222, 128, 0.22);
  border-radius: 10px;
  max-width: 560px;
}
.fb-thanks-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(74, 222, 128, 0.12);
  color: #4ade80;
  flex-shrink: 0;
  margin-top: 2px;
}
.fb-thanks-body {
  flex: 1;
  min-width: 0;
}
.fb-thanks-text {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 4px;
}
.fb-thanks-meta {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.45);
  margin: 0 0 8px;
}
.fb-thanks-stars {
  display: flex;
  gap: 2px;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.15);
}
.fb-thanks-stars svg.on {
  color: #d3ca57;
}
.fb-thanks-message {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
  margin: 0;
  overflow-wrap: break-word;
}

@media (max-width: 540px) {
  .fb-actions {
    flex-direction: column;
    align-items: stretch;
  }
  .fb-submit {
    width: 100%;
  }
}
</style>
